import { Component } from 'react'
import styled from 'styled-components'

const StyledInput = styled.input`
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.radius.input};
  font-size: ${props => props.theme.typography.fontSize.input};
  font-family: ${props => props.theme.typography.fontFamily};
  background: ${props => props.theme.colors.background.secondary};
  color: ${props => props.theme.colors.text.primary};
  transition: ${props => props.theme.transitions.default};
  text-align: right;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.border.focus};
    background: ${props => props.theme.colors.background.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.shadow.focus};
  }

  &::placeholder {
    color: ${props => props.theme.colors.text.muted};
    text-align: left;
  }

  &:disabled {
    background: ${props => props.theme.colors.background.disabled};
    cursor: not-allowed;
    opacity: 0.6;
  }

  &:hover:not(:focus):not(:disabled) {
    border-color: ${props => props.theme.colors.text.tertiary};
    background: ${props => props.theme.colors.background.hover};
  }
`

/**
 * CurrencyInput - Input formattato per valori monetari
 *
 * Formattazione italiana: 1.234.567,89
 * - Separatore migliaia: punto (.)
 * - Separatore decimali: virgola (,)
 * - Sempre 2 decimali
 *
 * @example
 * <CurrencyInput
 *   value={this.state.prezzo}
 *   onChange={(value) => this.setState({ prezzo: value })}
 *   placeholder="Inserisci importo"
 * />
 */
export default class CurrencyInput extends Component {
  constructor (props) {
    super(props)
    this.state = {
      displayValue: this.formatDisplay(props.value || 0)
    }
  }

  componentDidUpdate (prevProps) {
    // Aggiorna il display se il valore cambia dall'esterno
    if (prevProps.value !== this.props.value && !this.isFocused) {
      this.setState({
        displayValue: this.formatDisplay(this.props.value || 0)
      })
    }
  }

  /**
   * Formatta un numero per la visualizzazione
   * es: 1234567.89 -> "1.234.567,89"
   */
  formatDisplay (value) {
    if (value === '' || value === null || value === undefined) {
      return ''
    }

    const number = typeof value === 'string' ? parseFloat(value) : value

    if (isNaN(number)) {
      return ''
    }

    // Formatta con separatori italiani
    return new Intl.NumberFormat('it-IT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number)
  }

  /**
   * Converte il valore formattato in numero
   * es: "1.234.567,89" -> 1234567.89
   */
  parseValue (formattedValue) {
    if (!formattedValue || formattedValue === '') {
      return 0
    }

    // Rimuove i punti (separatori migliaia) e sostituisce la virgola con il punto
    const normalized = formattedValue
      .replace(/\./g, '') // Rimuove i punti
      .replace(',', '.') // Sostituisce virgola con punto

    const parsed = parseFloat(normalized)
    return isNaN(parsed) ? 0 : parsed
  }

  handleFocus = (e) => {
    this.isFocused = true
    e.target.select() // Seleziona tutto il testo per facilitare la modifica

    if (this.props.onFocus) {
      this.props.onFocus(e)
    }
  }

  handleBlur = (e) => {
    this.isFocused = false
    const numericValue = this.parseValue(this.state.displayValue)

    // Formatta con 2 decimali al blur
    this.setState({
      displayValue: this.formatDisplay(numericValue)
    })

    if (this.props.onBlur) {
      this.props.onBlur(e)
    }
  }

  formatWhileTyping = (value) => {
    // Controlla se termina con una virgola (l'utente sta per inserire decimali)
    const endsWithComma = value.endsWith(',')

    // Separa parte intera e decimale
    const parts = value.split(',')
    const integerPart = parts[0].replace(/\./g, '') // Rimuove i punti
    const decimalPart = parts[1] || ''

    // Se la parte intera è vuota, ritorna stringa vuota
    if (!integerPart) {
      return decimalPart ? ',' + decimalPart : ''
    }

    // Formatta la parte intera con i punti delle migliaia
    const formatted = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')

    // Ricombina con i decimali
    // Se termina con virgola ma non ci sono decimali, mantieni la virgola
    if (endsWithComma && !decimalPart) {
      return formatted + ','
    }

    return decimalPart ? formatted + ',' + decimalPart : formatted
  }

  handleChange = (e) => {
    const cursorPosition = e.target.selectionStart
    let inputValue = e.target.value

    // Permetti solo numeri, punto, virgola
    inputValue = inputValue.replace(/[^0-9.,]/g, '')

    // Conta i punti prima della posizione del cursore (per ricalcolare la posizione dopo)
    const beforeCursor = inputValue.substring(0, cursorPosition)
    const dotsBefore = (beforeCursor.match(/\./g) || []).length

    // Rimuovi tutti i punti (verranno riaggiunti dalla formattazione)
    const withoutDots = inputValue.replace(/\./g, '')

    // Permetti solo una virgola
    const parts = withoutDots.split(',')
    if (parts.length > 2) {
      inputValue = parts[0] + ',' + parts.slice(1).join('')
    } else {
      inputValue = withoutDots
    }

    // Limita i decimali a 2 cifre
    const finalParts = inputValue.split(',')
    if (finalParts.length === 2 && finalParts[1].length > 2) {
      inputValue = finalParts[0] + ',' + finalParts[1].substring(0, 2)
    }

    // Formatta con i punti delle migliaia
    const formattedValue = this.formatWhileTyping(inputValue)

    // Calcola la nuova posizione del cursore
    const formattedBeforeCursor = this.formatWhileTyping(inputValue.substring(0, cursorPosition - dotsBefore))
    const newCursorPosition = formattedBeforeCursor.length

    this.setState({ displayValue: formattedValue }, () => {
      // Ripristina la posizione del cursore
      if (e.target === document.activeElement) {
        e.target.setSelectionRange(newCursorPosition, newCursorPosition)
      }
    })

    // Notifica il componente padre con il valore numerico
    if (this.props.onChange) {
      const numericValue = this.parseValue(formattedValue)
      this.props.onChange(numericValue)
    }
  }

  handlePaste = (e) => {
    e.preventDefault()

    // Ottieni il testo incollato
    const pastedText = e.clipboardData.getData('text')

    // Sanitizza: permetti solo numeri, virgole, punti e spazi
    let sanitized = pastedText.replace(/[^0-9.,\s]/g, '')

    // Rimuovi spazi
    sanitized = sanitized.replace(/\s/g, '')

    // Normalizza i separatori: converti punti usati come decimali in virgole
    // Gestisci formati comuni:
    // - "1234.56" (formato US) -> "1234,56"
    // - "1.234,56" (formato IT) -> "1234,56"
    // - "1,234.56" (formato US con migliaia) -> "1234,56"

    // Se contiene sia punti che virgole, assumiamo formato IT (punto=migliaia, virgola=decimali)
    if (sanitized.includes('.') && sanitized.includes(',')) {
      sanitized = sanitized.replace(/\./g, '') // Rimuovi punti (migliaia)
      // La virgola rimane come separatore decimale
    } else if (sanitized.includes('.')) {
      // Se contiene solo punti, potrebbero essere migliaia o decimali
      const dots = (sanitized.match(/\./g) || []).length
      // Se c'è più di un punto, sono separatori di migliaia
      if (dots > 1) {
        sanitized = sanitized.replace(/\./g, '')
      } else {
        // Un solo punto: potrebbe essere decimale (formato US)
        // Contiamo le cifre dopo il punto
        const parts = sanitized.split('.')
        if (parts[1] && parts[1].length <= 2) {
          // Probabilmente decimale in formato US, converti in virgola
          sanitized = sanitized.replace('.', ',')
        } else {
          // Probabilmente separatore di migliaia, rimuovi
          sanitized = sanitized.replace('.', '')
        }
      }
    } else if (sanitized.includes(',')) {
      // Se contiene solo virgole
      const commas = (sanitized.match(/,/g) || []).length
      // Se c'è più di una virgola, rimuovi tutte tranne l'ultima
      if (commas > 1) {
        const lastCommaIndex = sanitized.lastIndexOf(',')
        sanitized = sanitized.replace(/,/g, '')
        sanitized = sanitized.slice(0, lastCommaIndex) + ',' + sanitized.slice(lastCommaIndex)
      }
    }

    // Limita i decimali a 2 cifre
    const parts = sanitized.split(',')
    if (parts.length === 2 && parts[1].length > 2) {
      sanitized = parts[0] + ',' + parts[1].substring(0, 2)
    }

    // Se il risultato è vuoto o non valido, non fare nulla
    if (!sanitized || sanitized === ',') {
      return
    }

    // Applica il valore sanitizzato
    const formattedValue = this.formatWhileTyping(sanitized)
    this.setState({ displayValue: formattedValue })

    // Notifica il componente padre con il valore numerico
    if (this.props.onChange) {
      const numericValue = this.parseValue(formattedValue)
      this.props.onChange(numericValue)
    }
  }

  handleKeyDown = (e) => {
    // Permetti: backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
      // Permetti: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X (Windows/Linux) e Cmd+A, Cmd+C, Cmd+V, Cmd+X (macOS)
      (e.keyCode === 65 && (e.ctrlKey === true || e.metaKey === true)) ||
      (e.keyCode === 67 && (e.ctrlKey === true || e.metaKey === true)) ||
      (e.keyCode === 86 && (e.ctrlKey === true || e.metaKey === true)) ||
      (e.keyCode === 88 && (e.ctrlKey === true || e.metaKey === true)) ||
      // Permetti: home, end, left, right
      (e.keyCode >= 35 && e.keyCode <= 39)) {
      return
    }

    // Permetti numeri sulla tastiera principale e sul numpad
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) &&
      (e.keyCode < 96 || e.keyCode > 105) &&
      // Permetti virgola e punto
      e.keyCode !== 188 && e.keyCode !== 190 && e.keyCode !== 110) {
      e.preventDefault()
    }

    if (this.props.onKeyDown) {
      this.props.onKeyDown(e)
    }
  }

  render () {
    const { value, onChange, onFocus, onBlur, onKeyDown, className, style, ...otherProps } = this.props
    const { displayValue } = this.state

    return (
      <StyledInput
        type='text'
        value={displayValue}
        onChange={this.handleChange}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        onKeyDown={this.handleKeyDown}
        onPaste={this.handlePaste}
        className={className}
        style={style}
        {...otherProps}
      />
    )
  }
}
